/** Local draft for the free meal plan wizard (refresh, tab close, OAuth redirect). */
const STORAGE_KEY = 'yumnia-fmp-draft'
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000 // 14 days
export const FREE_MEAL_PLAN_DRAFT_SCHEMA_VERSION = 7
export const FREE_MEAL_PLAN_RECOMMENDATIONS_SCHEMA_VERSION = 2

function migrateSessionDraftToLocal () {
  try {
    const fromSession = sessionStorage.getItem(STORAGE_KEY)
    if (fromSession && !localStorage.getItem(STORAGE_KEY)) {
      localStorage.setItem(STORAGE_KEY, fromSession)
    }
    if (fromSession) {
      sessionStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    // ignore
  }
}

/**
 * Map legacy 10-step wizard currentStep to 9-step wizard (schema v2).
 * @param {number} oldStep
 * @returns {number}
 */
export function migrateFreeMealPlanDraftStep (oldStep) {
  if (oldStep < 1 || oldStep > 10) return 1
  if (oldStep <= 3) return oldStep
  const map = { 4: 4, 5: 4, 6: 5, 7: 6, 8: 6, 9: 7, 10: 9 }
  return map[oldStep] ?? 1
}

/**
 * Map pre-v5 12-step wizard `currentStep` (schema v4 order) to v5 order (preferences + meal love before biometrics).
 * @param {number} oldStep
 * @returns {number}
 */
export function migrateV4WizardStepToV5 (oldStep) {
  const clamped = Math.max(1, Math.min(12, Math.floor(Number(oldStep)) || 1))
  const map = {
    1: 1,
    2: 5,
    3: 6,
    4: 7,
    5: 8,
    6: 2,
    7: 3,
    8: 9,
    9: 10,
    10: 11,
    11: 4,
    12: 12
  }
  return map[clamped] ?? clamped
}

/**
 * Map pre-v6 12-step wizard `currentStep` (schema v5 order) to v6 order (registration gate
 * promoted from step 12 to step 5; biometrics → fine-tune shifted from 5–11 to 6–12).
 * @param {number} oldStep
 * @returns {number}
 */
export function migrateV5WizardStepToV6 (oldStep) {
  const clamped = Math.max(1, Math.min(12, Math.floor(Number(oldStep)) || 1))
  const map = {
    1: 1,
    2: 2,
    3: 3,
    4: 4,
    5: 6,
    6: 7,
    7: 8,
    8: 9,
    9: 10,
    10: 11,
    11: 12,
    12: 5
  }
  return map[clamped] ?? clamped
}

/**
 * Map v6 12-step wizard `currentStep` to v7 (18 steps; eating, protein, and kitchen bundles split).
 * @param {number} oldStep
 * @returns {number}
 */
export function migrateV6WizardStepToV7 (oldStep) {
  const clamped = Math.max(1, Math.min(12, Math.floor(Number(oldStep)) || 1))
  const map = {
    1: 1,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 11,
    10: 12,
    11: 13,
    12: 16
  }
  return map[clamped] ?? clamped
}

/**
 * @param {{ formData: object, currentStep: number, refining?: boolean, dirtyFields?: string[] }} payload
 */
export function saveFreeMealPlanDraft (payload) {
  try {
    const dirtyFields = Array.isArray(payload.dirtyFields)
      ? payload.dirtyFields.filter(field => typeof field === 'string')
      : []
    const data = {
      schemaVersion: FREE_MEAL_PLAN_DRAFT_SCHEMA_VERSION,
      formData: payload.formData,
      currentStep: payload.currentStep,
      // Whether the user had opted past the essentials into "keep customising".
      // Without it a restored draft snaps back onto the essentials sequence and
      // the refine answers they already gave become unreachable.
      refining: payload.refining === true,
      dirtyFields,
      recommendationState: sanitizeRecommendationState(payload.recommendationState),
      savedAt: Date.now()
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    try {
      sessionStorage.removeItem(STORAGE_KEY)
    } catch {
      // ignore
    }
  } catch {
    // ignore quota / private mode
  }
}

/**
 * @returns {{ formData: object, currentStep: number, refining: boolean, dirtyFields?: string[] } | null}
 */
export function loadFreeMealPlanDraft () {
  migrateSessionDraftToLocal()
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed.savedAt !== 'number') {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }
    if (Date.now() - parsed.savedAt > MAX_AGE_MS) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }
    if (!parsed.formData || typeof parsed.currentStep !== 'number') {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }
    let step = parsed.currentStep
    const v = parsed.schemaVersion
    if (v === 6) {
      step = migrateV6WizardStepToV7(step)
    } else if (v === 5) {
      step = migrateV6WizardStepToV7(migrateV5WizardStepToV6(step))
    } else if (v === 4) {
      step = migrateV6WizardStepToV7(migrateV5WizardStepToV6(migrateV4WizardStepToV5(step)))
    } else if (v == null || v < 4) {
      step = migrateV6WizardStepToV7(migrateV5WizardStepToV6(migrateFreeMealPlanDraftStep(step)))
    }
    return {
      formData: parsed.formData,
      currentStep: step,
      // Drafts saved before this field existed default to the essentials path.
      refining: parsed.refining === true,
      dirtyFields: Array.isArray(parsed.dirtyFields)
        ? parsed.dirtyFields.filter(field => typeof field === 'string')
        : undefined,
      recommendationState: sanitizeRecommendationState(parsed.recommendationState)
    }
  } catch {
    return null
  }
}

/**
 * @param {any} state
 * @returns {{ schemaVersion: number, initialRecipes: object[], similarByParent: Record<string, object[]>, seenRecipeIds: number[], selectedRecipeCache: object[] } | null}
 */
function sanitizeRecommendationState (state) {
  if (!state || typeof state !== 'object') {
    return null
  }

  const initialRecipes = Array.isArray(state.initialRecipes)
    ? state.initialRecipes.filter(recipe => recipe && typeof recipe === 'object')
    : []

  const similarByParent = {}
  if (state.similarByParent && typeof state.similarByParent === 'object' && !Array.isArray(state.similarByParent)) {
    for (const [key, value] of Object.entries(state.similarByParent)) {
      const parentId = Number(key)
      if (!Number.isInteger(parentId) || parentId <= 0 || !Array.isArray(value)) continue
      similarByParent[parentId] = value.filter(recipe => recipe && typeof recipe === 'object')
    }
  }

  const seenRecipeIds = Array.isArray(state.seenRecipeIds)
    ? Array.from(new Set(state.seenRecipeIds.map(Number).filter(id => Number.isInteger(id) && id > 0)))
    : []

  const selectedRecipeCache = []
  if (Array.isArray(state.selectedRecipeCache)) {
    const seenIds = new Set()
    for (const recipe of state.selectedRecipeCache) {
      if (!recipe || typeof recipe !== 'object') continue
      const id = Number(recipe.id)
      if (!Number.isInteger(id) || id <= 0 || seenIds.has(id)) continue
      seenIds.add(id)
      selectedRecipeCache.push(recipe)
    }
  }

  if (
    initialRecipes.length === 0 &&
    Object.keys(similarByParent).length === 0 &&
    seenRecipeIds.length === 0 &&
    selectedRecipeCache.length === 0
  ) {
    return null
  }

  return {
    schemaVersion: FREE_MEAL_PLAN_RECOMMENDATIONS_SCHEMA_VERSION,
    initialRecipes,
    similarByParent,
    seenRecipeIds,
    selectedRecipeCache
  }
}

export function clearFreeMealPlanDraft () {
  try {
    localStorage.removeItem(STORAGE_KEY)
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

const OAUTH_AUTO_SUBMIT_KEY = 'yumnia-fmp-oauth-auto-submit'
export const OAUTH_AUTO_SUBMIT_MAX_AGE_MS = 30 * 60 * 1000 // 30 minutes

export function setPendingOAuthMealPlanSubmit () {
  try {
    sessionStorage.setItem(OAUTH_AUTO_SUBMIT_KEY, JSON.stringify({ setAt: Date.now() }))
  } catch {
    // ignore
  }
}

export function clearPendingOAuthMealPlanSubmit () {
  try {
    sessionStorage.removeItem(OAUTH_AUTO_SUBMIT_KEY)
  } catch {
    // ignore
  }
}

/**
 * @param {number} [maxAgeMs]
 * @returns {boolean} true if a fresh intent was present (and removed)
 */
export function consumePendingOAuthMealPlanSubmit (maxAgeMs = OAUTH_AUTO_SUBMIT_MAX_AGE_MS) {
  try {
    const raw = sessionStorage.getItem(OAUTH_AUTO_SUBMIT_KEY)
    if (!raw) return false
    sessionStorage.removeItem(OAUTH_AUTO_SUBMIT_KEY)
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed.setAt !== 'number') {
      return false
    }
    if (Date.now() - parsed.setAt > maxAgeMs) {
      return false
    }
    return true
  } catch {
    try {
      sessionStorage.removeItem(OAUTH_AUTO_SUBMIT_KEY)
    } catch {
      // ignore
    }
    return false
  }
}
